import React, { useEffect, useRef } from 'react';
import { BarChart2 } from 'lucide-react';

interface RiskProspect {
  id: string;
  lead_status: string;
  risk_score: number;
  // Other properties not needed for this component
  [key: string]: any;
}

interface ConversionMetricsChartProps {
  prospects: RiskProspect[];
}

const ConversionMetricsChart: React.FC<ConversionMetricsChartProps> = ({ prospects }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !prospects.length) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    // Group prospects by risk score range and count conversions
    const riskRanges = [
      { min: 0, max: 20, label: '0-20' },
      { min: 21, max: 40, label: '21-40' },
      { min: 41, max: 60, label: '41-60' },
      { min: 61, max: 80, label: '61-80' },
      { min: 81, max: 100, label: '81-100' }
    ];

    const conversionData = riskRanges.map(range => {
      const prospectsInRange = prospects.filter(
        p => p.risk_score >= range.min && p.risk_score <= range.max
      );
      
      const total = prospectsInRange.length;
      const converted = prospectsInRange.filter(p => p.lead_status === 'converted').length;
      const conversionRate = total > 0 ? (converted / total) * 100 : 0;
      
      return {
        ...range,
        total,
        converted,
        conversionRate
      };
    });

    // Canvas dimensions
    const width = canvasRef.current.width;
    const height = canvasRef.current.height;
    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    
    // Bar width and spacing
    const barWidth = chartWidth / riskRanges.length * 0.7;
    const barSpacing = chartWidth / riskRanges.length * 0.3;
    
    // Find max conversion rate for scaling
    const maxRate = Math.max(...conversionData.map(d => d.conversionRate));
    const yScale = chartHeight / (Math.ceil(maxRate / 10) * 10);
    
    // Draw bars
    conversionData.forEach((data, i) => {
      const barHeight = data.conversionRate * yScale;
      const x = padding.left + i * (barWidth + barSpacing) + barSpacing / 2;
      const y = height - padding.bottom - barHeight;
      
      // Draw bar
      ctx.fillStyle = '#3B82F6'; // Blue-500
      ctx.fillRect(x, y, barWidth, barHeight);
      
      // Draw label
      ctx.fillStyle = '#374151'; // Gray-700
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(data.label, x + barWidth / 2, height - padding.bottom + 15);
      
      // Draw conversion rate
      if (data.conversionRate > 0) {
        ctx.fillStyle = '#374151'; // Gray-700
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(data.conversionRate)}%`, x + barWidth / 2, y - 5);
      }
      
      // Draw total count
      if (data.total > 0) {
        ctx.fillStyle = '#6B7280'; // Gray-500
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`(${data.converted}/${data.total})`, x + barWidth / 2, y - 20);
      }
    });
    
    // Draw axes
    ctx.strokeStyle = '#E5E7EB'; // Gray-200
    ctx.lineWidth = 1;
    
    // X-axis
    ctx.beginPath();
    ctx.moveTo(padding.left, height - padding.bottom);
    ctx.lineTo(width - padding.right, height - padding.bottom);
    ctx.stroke();
    
    // Y-axis
    ctx.beginPath();
    ctx.moveTo(padding.left, padding.top);
    ctx.lineTo(padding.left, height - padding.bottom);
    ctx.stroke();
    
    // Y-axis ticks and labels
    const yTicks = 5;
    const yTickStep = Math.ceil(maxRate / 10) * 10 / yTicks;
    
    for (let i = 0; i <= yTicks; i++) {
      const yValue = i * yTickStep;
      const yPos = height - padding.bottom - yValue * yScale;
      
      // Draw tick
      ctx.beginPath();
      ctx.moveTo(padding.left - 5, yPos);
      ctx.lineTo(padding.left, yPos);
      ctx.stroke();
      
      // Draw label
      ctx.fillStyle = '#6B7280'; // Gray-500
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${Math.round(yValue)}%`, padding.left - 8, yPos);
    }
    
    // Y-axis title
    ctx.save();
    ctx.fillStyle = '#374151'; // Gray-700
    ctx.font = '12px sans-serif';
    ctx.translate(15, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.fillText('Conversion Rate', 0, 0);
    ctx.restore();
    
    // X-axis title
    ctx.fillStyle = '#374151'; // Gray-700
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Risk Score Range', width / 2, height - 5);
    
  }, [prospects]);

  if (!prospects.length) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400">
        <BarChart2 className="h-12 w-12 mb-2" />
        <p>No data available</p>
      </div>
    );
  }

  return (
    <canvas 
      ref={canvasRef} 
      width={500} 
      height={250}
      className="w-full h-full"
    />
  );
};

export default ConversionMetricsChart;